import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Search, X } from '@tamagui/lucide-icons'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useAppStore } from '@/store'
import { customEmojis } from '@/mocks'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 5000,
})

const Picker = styled(View, {
  position: 'absolute',
  bottom: 80,
  right: 24,
  width: 352,
  height: 420,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 5001,
})

const PickerHeader = styled(View, {
  paddingHorizontal: 12,
  paddingVertical: 10,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 8,
})

const TabBar = styled(View, {
  flexDirection: 'row',
  gap: 0,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const Tab = styled(View, {
  flex: 1,
  height: 36,
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderBottomWidth: 2,
  borderBottomColor: 'transparent',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  variants: {
    active: {
      true: {
        borderBottomColor: '$accent',
      },
    },
  } as const,
})

const SearchBox = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusMd',
  paddingHorizontal: 8,
  height: 32,
  gap: 6,
})

const CategoryTabs = styled(View, {
  flexDirection: 'row',
  gap: 2,
  paddingTop: 4,
})

const CategoryTab = styled(View, {
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: '$borderRadiusSm',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$sidebarMuted',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 8,
  paddingVertical: 6,
})

const EmojiGrid = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  paddingHorizontal: 4,
})

const EmojiButton = styled(View, {
  width: 36,
  height: 36,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const GifGrid = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  padding: 8,
  gap: 8,
})

const GifItem = styled(View, {
  width: 100,
  height: 80,
  borderRadius: '$borderRadiusMd',
  overflow: 'hidden',
  cursor: 'pointer',
  hoverStyle: { opacity: 0.8 },
})

// Emoji name map for search
const emojiNameMap: Record<string, string[]> = {
  '😀': ['grinning', 'happy', 'smile'],
  '😃': ['smiley', 'happy', 'smile'],
  '😄': ['smile', 'happy', 'grin'],
  '😁': ['grin', 'beam', 'happy'],
  '😅': ['sweat', 'smile', 'nervous'],
  '😂': ['joy', 'laugh', 'cry', 'tears'],
  '🤣': ['rofl', 'laugh', 'rolling'],
  '😊': ['blush', 'smile', 'happy'],
  '😇': ['innocent', 'angel', 'halo'],
  '🙂': ['slight smile', 'smile'],
  '😉': ['wink'],
  '😌': ['relieved', 'peaceful'],
  '😍': ['heart eyes', 'love'],
  '🥰': ['smiling hearts', 'love', 'adore'],
  '😘': ['kiss', 'blow kiss'],
  '😗': ['kissing'],
  '😙': ['kiss', 'smile'],
  '😚': ['kiss', 'closed eyes'],
  '😋': ['yummy', 'tongue', 'delicious'],
  '😛': ['tongue', 'playful'],
  '😝': ['squinting tongue', 'playful'],
  '😜': ['wink tongue', 'crazy'],
  '🤪': ['zany', 'crazy', 'wild'],
  '🤨': ['raised eyebrow', 'skeptic'],
  '🧐': ['monocle', 'thinking', 'curious'],
  '🤓': ['nerd', 'glasses', 'geek'],
  '😎': ['cool', 'sunglasses'],
  '🤩': ['star struck', 'excited', 'wow'],
  '🥳': ['party', 'celebrate', 'birthday'],
  '😏': ['smirk'],
  '😒': ['unamused', 'meh'],
  '😞': ['disappointed', 'sad'],
  '😔': ['pensive', 'sad', 'thoughtful'],
  '😟': ['worried', 'concerned'],
  '😕': ['confused', 'unsure'],
  '🙁': ['frown', 'sad'],
  '😣': ['persevere', 'struggle'],
  '😖': ['confounded', 'frustrated'],
  '😫': ['tired', 'exhausted'],
  '😩': ['weary', 'tired'],
  '🥺': ['pleading', 'puppy eyes', 'please'],
  '😢': ['cry', 'sad', 'tear'],
  '😭': ['sob', 'cry', 'bawl'],
  '😤': ['angry', 'huff', 'steam'],
  '😠': ['angry', 'mad'],
  '😡': ['rage', 'furious', 'angry'],
  '🤬': ['cursing', 'swear', 'angry'],
  '🤯': ['mind blown', 'exploding', 'shocked'],
  '😳': ['flushed', 'embarrassed', 'shocked'],
  '🥵': ['hot', 'sweating'],
  '🥶': ['cold', 'freezing'],
  '😱': ['scream', 'scared', 'horror'],
  '😨': ['fearful', 'scared', 'afraid'],
  '😰': ['anxious', 'sweat', 'nervous'],
  '😥': ['sad', 'relieved', 'disappointed'],
  '😓': ['downcast', 'sweat'],
  '🤗': ['hug', 'hugging'],
  '🤔': ['thinking', 'hmm', 'consider'],
  '🤭': ['hand over mouth', 'oops'],
  '🤫': ['shush', 'quiet', 'secret'],
  '🤥': ['lying', 'pinocchio'],
  '😶': ['no mouth', 'silent', 'mute'],
  '😐': ['neutral', 'straight face'],
  '😑': ['expressionless', 'blank'],
  '👋': ['wave', 'hello', 'hi', 'bye'],
  '🤚': ['raised back hand', 'stop'],
  '🖐️': ['hand', 'five', 'high five'],
  '✋': ['stop', 'hand', 'high five'],
  '🖖': ['vulcan', 'spock'],
  '👌': ['ok', 'perfect', 'fine'],
  '🤌': ['pinched fingers', 'italian'],
  '🤏': ['pinching', 'small', 'tiny'],
  '✌️': ['peace', 'victory'],
  '🤞': ['crossed fingers', 'luck', 'hope'],
  '🤟': ['love you', 'rock on'],
  '🤘': ['rock', 'metal', 'horns'],
  '🤙': ['call me', 'hang loose', 'shaka'],
  '👈': ['point left'],
  '👉': ['point right'],
  '👆': ['point up'],
  '🖕': ['middle finger'],
  '👇': ['point down'],
  '☝️': ['point up', 'one'],
  '👍': ['thumbs up', 'like', 'yes', 'good'],
  '👎': ['thumbs down', 'dislike', 'no', 'bad'],
  '✊': ['fist', 'power'],
  '👊': ['punch', 'fist bump'],
  '🤛': ['left fist'],
  '🤜': ['right fist'],
  '👏': ['clap', 'applause', 'bravo'],
  '🙌': ['raised hands', 'hooray', 'celebration'],
  '👐': ['open hands'],
  '🤲': ['palms up'],
  '🤝': ['handshake', 'deal', 'agree'],
  '🙏': ['pray', 'please', 'thank you', 'hope'],
  '💪': ['muscle', 'strong', 'flex', 'bicep'],
  '🌱': ['seedling', 'sprout', 'plant'],
  '🌿': ['herb', 'leaf', 'plant'],
  '☘️': ['shamrock', 'clover'],
  '🍀': ['four leaf clover', 'lucky'],
  '🌵': ['cactus', 'desert'],
  '🌲': ['evergreen', 'tree', 'pine'],
  '🌳': ['tree', 'deciduous'],
  '🌴': ['palm tree', 'tropical'],
  '🌾': ['rice', 'wheat', 'grain'],
  '🌺': ['hibiscus', 'flower'],
  '🌻': ['sunflower'],
  '🌹': ['rose', 'flower', 'love'],
  '🥀': ['wilted flower', 'dead'],
  '🌷': ['tulip', 'flower'],
  '🌸': ['cherry blossom', 'sakura'],
  '💐': ['bouquet', 'flowers'],
  '🍄': ['mushroom'],
  '🐶': ['dog', 'puppy'],
  '🐱': ['cat', 'kitty'],
  '🐭': ['mouse'],
  '🐹': ['hamster'],
  '🐰': ['rabbit', 'bunny'],
  '🦊': ['fox'],
  '🐻': ['bear'],
  '🐼': ['panda'],
  '🐨': ['koala'],
  '🐯': ['tiger'],
  '🦁': ['lion'],
  '🐮': ['cow'],
  '🐷': ['pig'],
  '🐸': ['frog'],
  '🐵': ['monkey'],
  '🍎': ['apple', 'red apple'],
  '🍐': ['pear'],
  '🍊': ['orange', 'tangerine'],
  '🍋': ['lemon'],
  '🍌': ['banana'],
  '🍉': ['watermelon'],
  '🍇': ['grapes'],
  '🍓': ['strawberry'],
  '🫐': ['blueberry'],
  '🍈': ['melon'],
  '🍒': ['cherry'],
  '🍑': ['peach'],
  '🥭': ['mango'],
  '🍍': ['pineapple'],
  '🥥': ['coconut'],
  '🥝': ['kiwi'],
  '🍅': ['tomato'],
  '🍕': ['pizza'],
  '🍔': ['hamburger', 'burger'],
  '🍟': ['fries', 'french fries'],
  '🌭': ['hot dog'],
  '🍿': ['popcorn'],
  '🧁': ['cupcake'],
  '🍰': ['cake', 'shortcake'],
  '☕': ['coffee', 'hot drink'],
  '🍵': ['tea'],
  '🍺': ['beer'],
  '🍻': ['beers', 'cheers'],
  '🥂': ['champagne', 'toast', 'cheers'],
  '🍷': ['wine'],
  '🥤': ['cup with straw', 'drink'],
  '🧃': ['juice box'],
  '💡': ['light bulb', 'idea'],
  '🔦': ['flashlight'],
  '🕯️': ['candle'],
  '💻': ['laptop', 'computer'],
  '🖥️': ['desktop', 'computer', 'monitor'],
  '⌨️': ['keyboard'],
  '🖱️': ['mouse', 'computer mouse'],
  '📱': ['phone', 'mobile', 'smartphone'],
  '📞': ['telephone'],
  '☎️': ['telephone'],
  '📺': ['tv', 'television'],
  '📻': ['radio'],
  '🎙️': ['microphone', 'mic'],
  '🎚️': ['level slider'],
  '🎛️': ['control knobs'],
  '⏰': ['alarm', 'clock'],
  '🔒': ['lock', 'locked', 'secure'],
  '🔓': ['unlock', 'unlocked'],
  '🔑': ['key'],
  '🗝️': ['old key'],
  '🛡️': ['shield', 'protect'],
  '🔐': ['locked with key'],
  '📫': ['mailbox'],
  '📪': ['mailbox closed'],
  '💰': ['money bag', 'rich'],
  '💎': ['gem', 'diamond'],
  '🔧': ['wrench', 'tool'],
  '🔨': ['hammer', 'tool'],
  '⚙️': ['gear', 'settings', 'cog'],
  '🧲': ['magnet'],
  '🔬': ['microscope', 'science'],
  '🔭': ['telescope'],
  '❤️': ['heart', 'love', 'red heart'],
  '🧡': ['orange heart'],
  '💛': ['yellow heart'],
  '💚': ['green heart'],
  '💙': ['blue heart'],
  '💜': ['purple heart'],
  '🖤': ['black heart'],
  '🤍': ['white heart'],
  '💔': ['broken heart'],
  '❣️': ['heart exclamation'],
  '💕': ['two hearts'],
  '💞': ['revolving hearts'],
  '💓': ['heartbeat'],
  '💗': ['growing heart'],
  '💖': ['sparkling heart'],
  '💘': ['heart with arrow', 'cupid'],
  '💝': ['heart with ribbon', 'gift heart'],
  '🔥': ['fire', 'hot', 'lit'],
  '⭐': ['star'],
  '🌟': ['glowing star', 'sparkle'],
  '✨': ['sparkles', 'shine'],
  '⚡': ['lightning', 'zap', 'electric'],
  '💥': ['boom', 'explosion', 'collision'],
  '💫': ['dizzy', 'star'],
  '✅': ['check', 'yes', 'done', 'complete'],
  '❌': ['cross', 'no', 'wrong', 'x'],
  '⚠️': ['warning', 'caution'],
  '🚫': ['prohibited', 'no', 'forbidden'],
  '♻️': ['recycle'],
  '💯': ['hundred', 'perfect', '100'],
  '🔴': ['red circle'],
  '🟢': ['green circle'],
  '🏳️': ['white flag'],
  '🏴': ['black flag'],
  '🏁': ['checkered flag', 'finish'],
  '🚩': ['red flag', 'triangular flag'],
  '🏳️‍🌈': ['rainbow flag', 'pride'],
  '🏳️‍⚧️': ['transgender flag'],
  '🏴‍☠️': ['pirate flag', 'jolly roger'],
  '🇺🇸': ['us', 'usa', 'america'],
  '🇬🇧': ['uk', 'britain', 'gb'],
  '🇨🇦': ['canada'],
  '🇦🇺': ['australia'],
  '🇩🇪': ['germany'],
  '🇫🇷': ['france'],
  '🇯🇵': ['japan'],
  '🇰🇷': ['korea', 'south korea'],
  '🇧🇷': ['brazil'],
  '🕐': ['clock', 'time', 'one o\'clock'],
}

const emojiCategories = [
  {
    id: 'recent',
    icon: '🕐',
    label: 'Recently Used',
    emojis: ['👍', '❤️', '😂', '🔥', '👀', '✅', '🎉', '💯'],
  },
  {
    id: 'smileys',
    icon: '😀',
    label: 'Smileys & People',
    emojis: [
      '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
      '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗',
      '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨',
      '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞',
      '😔', '😟', '😕', '🙁', '😣', '😖', '😫', '😩',
      '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯',
      '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓',
      '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑',
    ],
  },
  {
    id: 'gestures',
    icon: '👋',
    label: 'Gestures & Body',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏',
      '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆',
      '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛',
      '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪',
    ],
  },
  {
    id: 'nature',
    icon: '🌿',
    label: 'Nature',
    emojis: [
      '🌱', '🌿', '☘️', '🍀', '🌵', '🌲', '🌳', '🌴',
      '🌾', '🌺', '🌻', '🌹', '🥀', '🌷', '🌸', '💐',
      '🍄', '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻',
      '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵',
    ],
  },
  {
    id: 'food',
    icon: '🍕',
    label: 'Food & Drink',
    emojis: [
      '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓',
      '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝',
      '🍅', '🍕', '🍔', '🍟', '🌭', '🍿', '🧁', '🍰',
      '☕', '🍵', '🍺', '🍻', '🥂', '🍷', '🥤', '🧃',
    ],
  },
  {
    id: 'objects',
    icon: '💡',
    label: 'Objects',
    emojis: [
      '💡', '🔦', '🕯️', '💻', '🖥️', '⌨️', '🖱️', '📱',
      '📞', '☎️', '📺', '📻', '🎙️', '🎚️', '🎛️', '⏰',
      '🔒', '🔓', '🔑', '🗝️', '🛡️', '🔐', '📫', '📪',
      '💰', '💎', '🔧', '🔨', '⚙️', '🧲', '🔬', '🔭',
    ],
  },
  {
    id: 'symbols',
    icon: '❤️',
    label: 'Symbols',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘',
      '💝', '🔥', '⭐', '🌟', '✨', '⚡', '💥', '💫',
      '✅', '❌', '⚠️', '🚫', '♻️', '💯', '🔴', '🟢',
    ],
  },
  {
    id: 'flags',
    icon: '🏳️',
    label: 'Flags',
    emojis: [
      '🏳️', '🏴', '🏁', '🚩', '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️', '🇺🇸',
      '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷', '🇯🇵', '🇰🇷', '🇧🇷',
    ],
  },
]

const mockGifs = Array.from({ length: 16 }, (_, i) => ({
  id: `gif-${i + 1}`,
  url: `https://placehold.co/200x150/1A1F2E/3ECFCF?text=GIF+${i + 1}`,
  title: `GIF ${i + 1}`,
}))

const mockStickers = Array.from({ length: 12 }, (_, i) => ({
  id: `sticker-${i + 1}`,
  name: `sticker_${i + 1}`,
  url: `https://placehold.co/80x80/1A1F2E/E8A840?text=S${i + 1}`,
}))

const StickerGrid = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  padding: 8,
  gap: 8,
  justifyContent: 'center',
})

const StickerItem = styled(View, {
  width: 80,
  height: 80,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

interface EmojiPickerProps {
  onSelect: (emoji: string) => void
}

export function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const { setEmojiPickerOpen, activeServerId } = useAppStore()
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('server')
  const [activeTab, setActiveTab] = useState<'emoji' | 'gif' | 'sticker'>('emoji')
  const [gifSearch, setGifSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const serverEmojis = activeServerId ? customEmojis[activeServerId] || [] : []

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEmojiPickerOpen(false)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [setEmojiPickerOpen])

  const filteredCategories = useMemo(() => {
    if (!search) return emojiCategories
    const q = search.toLowerCase()
    return emojiCategories.map(cat => ({
      ...cat,
      emojis: cat.emojis.filter((emoji) => {
        const names = emojiNameMap[emoji]
        if (!names) return false
        return names.some(name => name.includes(q))
      }),
    })).filter(cat => cat.emojis.length > 0)
  }, [search])

  const filteredServerEmojis = useMemo(() => {
    if (!search) return serverEmojis
    const q = search.toLowerCase()
    return serverEmojis.filter(e => e.name.toLowerCase().includes(q))
  }, [search, serverEmojis])

  const filteredGifs = useMemo(() => {
    if (!gifSearch) return mockGifs
    return mockGifs.filter(g => g.title.toLowerCase().includes(gifSearch.toLowerCase()))
  }, [gifSearch])

  const handleSelect = (emoji: string) => {
    onSelect(emoji)
    setEmojiPickerOpen(false)
  }

  return (
    <>
      <Overlay onPress={() => setEmojiPickerOpen(false)} />
      <Picker
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowCard?.val || 'none' }}
      >
        <TabBar>
          <Tab active={activeTab === 'emoji'} onPress={() => setActiveTab('emoji')}>
            <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={activeTab === 'emoji' ? '$accent' : '$sidebarMuted'}>
              Emoji
            </Text>
          </Tab>
          <Tab active={activeTab === 'gif'} onPress={() => setActiveTab('gif')}>
            <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={activeTab === 'gif' ? '$accent' : '$sidebarMuted'}>
              GIFs
            </Text>
          </Tab>
          <Tab active={activeTab === 'sticker'} onPress={() => setActiveTab('sticker')}>
            <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={activeTab === 'sticker' ? '$accent' : '$sidebarMuted'}>
              Stickers
            </Text>
          </Tab>
        </TabBar>

        {activeTab === 'sticker' ? (
          <ScrollView flex={1} showsVerticalScrollIndicator={false}>
            <View paddingHorizontal={8} paddingBottom={4}>
              <SectionLabel>Stickers</SectionLabel>
            </View>
            <StickerGrid>
              {mockStickers.map((sticker) => (
                <StickerItem
                  key={sticker.id}
                  onPress={() => {
                    onSelect(`:${sticker.name}:`)
                    setEmojiPickerOpen(false)
                  }}
                >
                  <img
                    src={sticker.url}
                    alt={sticker.name}
                    style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'contain' }}
                  />
                </StickerItem>
              ))}
            </StickerGrid>
          </ScrollView>
        ) : activeTab === 'emoji' ? (
          <>
            <PickerHeader>
              <SearchBox>
                <Search size={14} color="$sidebarMuted" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search emoji..."
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'inherit',
                    fontFamily: '$fontBody',
                    fontSize: 13,
                    width: '100%',
                  }}
                />
                {search && (
                  <View cursor="pointer" onPress={() => setSearch('')}>
                    <X size={14} color="$sidebarMuted" />
                  </View>
                )}
              </SearchBox>
              <CategoryTabs>
                {serverEmojis.length > 0 && (
                  <CategoryTab
                    onPress={() => setActiveCategory('server')}
                    backgroundColor={activeCategory === 'server' ? '$sidebarActive' : 'transparent'}
                  >
                    <Text fontSize={16}>⚙️</Text>
                  </CategoryTab>
                )}
                {emojiCategories.map((cat) => (
                  <CategoryTab
                    key={cat.id}
                    onPress={() => setActiveCategory(cat.id)}
                    backgroundColor={activeCategory === cat.id ? '$sidebarActive' : 'transparent'}
                  >
                    <Text fontSize={16}>{cat.icon}</Text>
                  </CategoryTab>
                ))}
              </CategoryTabs>
            </PickerHeader>

            <ScrollView flex={1} showsVerticalScrollIndicator={false}>
              <View padding={4}>
                {/* Server emoji section */}
                {filteredServerEmojis.length > 0 && (
                  <View>
                    <SectionLabel>Server Emoji</SectionLabel>
                    <EmojiGrid>
                      {filteredServerEmojis.map((emoji) => (
                        <EmojiButton
                          key={emoji.id}
                          onPress={() => handleSelect(`:${emoji.name}:`)}
                          // @ts-expect-error -- web title
                          title={`:${emoji.name}:`}
                        >
                          <img
                            src={emoji.imageUrl}
                            alt={emoji.name}
                            style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain' }}
                          />
                        </EmojiButton>
                      ))}
                    </EmojiGrid>
                  </View>
                )}

                {/* Standard emoji categories */}
                {filteredCategories.map((cat) => (
                  <View key={cat.id}>
                    <SectionLabel>{cat.label}</SectionLabel>
                    <EmojiGrid>
                      {cat.emojis.map((emoji, i) => (
                        <EmojiButton key={`${cat.id}-${i}`} onPress={() => handleSelect(emoji)}>
                          <Text fontSize={22}>{emoji}</Text>
                        </EmojiButton>
                      ))}
                    </EmojiGrid>
                  </View>
                ))}

                {filteredCategories.length === 0 && filteredServerEmojis.length === 0 && (
                  <View alignItems="center" justifyContent="center" padding={32}>
                    <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
                      No emoji found for "{search}"
                    </Text>
                  </View>
                )}
              </View>
            </ScrollView>
          </>
        ) : (
          <>
            <View paddingHorizontal={12} paddingVertical={10}>
              <SearchBox>
                <Search size={14} color="$sidebarMuted" />
                <input
                  value={gifSearch}
                  onChange={(e) => setGifSearch(e.target.value)}
                  placeholder="Search GIFs..."
                  style={{
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'inherit',
                    fontFamily: '$fontBody',
                    fontSize: 13,
                    width: '100%',
                  }}
                />
                {gifSearch && (
                  <View cursor="pointer" onPress={() => setGifSearch('')}>
                    <X size={14} color="$sidebarMuted" />
                  </View>
                )}
              </SearchBox>
            </View>

            <ScrollView flex={1} showsVerticalScrollIndicator={false}>
              <View paddingHorizontal={8} paddingBottom={4}>
                <SectionLabel>{gifSearch ? 'Search Results' : 'Trending'}</SectionLabel>
              </View>
              <GifGrid>
                {filteredGifs.map((gif) => (
                  <GifItem
                    key={gif.id}
                    onPress={() => {
                      onSelect(gif.url)
                      setEmojiPickerOpen(false)
                    }}
                  >
                    <img
                      src={gif.url}
                      alt={gif.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                    />
                  </GifItem>
                ))}
              </GifGrid>
              {filteredGifs.length === 0 && (
                <View alignItems="center" justifyContent="center" padding={32}>
                  <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
                    No GIFs found for "{gifSearch}"
                  </Text>
                </View>
              )}
            </ScrollView>
          </>
        )}
      </Picker>
    </>
  )
}
